import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiJson } from './apiClient';
import { getLocalDateKey } from './utils/reportDates';
import { wakalaCashbookRows } from './wakalaConstants';

const CashContext = createContext();

export function useCash() {
  return useContext(CashContext);
}

export function CashProvider({ children }) {
  const { user } = useAuth();
  const [floatBalance, setFloatBalance] = useState(0);
  const [cashBalance, setCashBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [openings, setOpenings] = useState({
    physicalCashOpening: 0,
    networkFloatOpening: 0,
    floatByChannel: {},
  });

  const refresh = useCallback(async () => {
    try {
      const data = await apiJson('/cashbook');
      setFloatBalance(Number(data.floatBalance) || 0);
      setCashBalance(data.cashBalance == null ? null : Number(data.cashBalance));
      setTransactions(Array.isArray(data.transactions) ? data.transactions : []);
      if (data.openings && typeof data.openings === 'object') {
        setOpenings({
          physicalCashOpening: Number(data.openings.physicalCashOpening) || 0,
          networkFloatOpening: Number(data.openings.networkFloatOpening) || 0,
          floatByChannel:
            data.openings.floatByChannel && typeof data.openings.floatByChannel === 'object'
              ? data.openings.floatByChannel
              : {},
        });
      } else {
        setOpenings({ physicalCashOpening: 0, networkFloatOpening: 0, floatByChannel: {} });
      }
    } catch {
      setFloatBalance(0);
      setCashBalance(null);
      setTransactions([]);
      setOpenings({ physicalCashOpening: 0, networkFloatOpening: 0, floatByChannel: {} });
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setFloatBalance(0);
      setCashBalance(null);
      setTransactions([]);
      setOpenings({ physicalCashOpening: 0, networkFloatOpening: 0, floatByChannel: {} });
      return;
    }
    void refresh();
  }, [user, refresh]);

  const stats = useMemo(() => {
    const todayKey = getLocalDateKey();
    const wakalaTx = wakalaCashbookRows(transactions);
    const todays = wakalaTx.filter((t) => String(t.dateKey) === todayKey);
    const cashIn = todays.filter((t) => t.direction === 'in').reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const cashOut = todays.filter((t) => t.direction === 'out').reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const commission = todays.reduce((s, t) => s + (Number(t.commission) || 0), 0);
    return { cashIn, cashOut, commission, todayCount: todays.length };
  }, [transactions]);

  const addTransaction = useCallback(
    async ({ direction, service, channel, amount, commission, commissionPercent, note }) => {
      const amt = Number(amount);
      if (!amt || amt <= 0) throw new Error('Invalid amount');
      const body = {
        direction,
        service,
        channel,
        amount: amt,
        note: note || '',
      };
      const commTzs = Number(commission);
      if (Number.isFinite(commTzs) && commTzs > 0) {
        body.commission = Math.round(commTzs * 100) / 100;
      } else {
        const pct = Number(commissionPercent);
        if (Number.isFinite(pct) && pct > 0) body.commissionPercent = pct;
      }
      await apiJson('/cashbook/transactions', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      await refresh();
    },
    [refresh]
  );

  const resetCashbook = useCallback(async () => {
    await apiJson('/cashbook/reset', { method: 'POST', body: '{}' });
    await refresh();
  }, [refresh]);

  return (
    <CashContext.Provider
      value={{
        floatBalance,
        cashBalance,
        transactions,
        openings,
        stats,
        addTransaction,
        resetCashbook,
        refreshCashbook: refresh,
      }}
    >
      {children}
    </CashContext.Provider>
  );
}
